'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ContactFormProps {
  currentLang?: 'sl' | 'en';
}

export default function ContactForm({ currentLang = 'sl' }: ContactFormProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const supabase = createClient();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const labels = {
    name: currentLang === 'sl' ? 'Ime in priimek' : 'Full Name',
    email: currentLang === 'sl' ? 'Email naslov' : 'Email Address',
    phone: currentLang === 'sl' ? 'Telefonska številka' : 'Phone Number',
    inquiryType: currentLang === 'sl' ? 'Vrsta poizvedbe' : 'Inquiry Type',
    message: currentLang === 'sl' ? 'Sporočilo' : 'Message',
    submit: currentLang === 'sl' ? 'Pošlji poizvedbo' : 'Send Inquiry',
    success:
      currentLang === 'sl' ?'Hvala! Vaša poizvedba je bila poslana. Kontaktirali vas bomo v 24 urah.' :'Thank you! Your inquiry has been sent. We will contact you within 24 hours.',
    sending: currentLang === 'sl' ? 'Pošiljanje …' : 'Sending…',
    sendError:
      currentLang === 'sl'
        ? 'Pošiljanje ni uspelo. Poskusite znova ali nas pokličite.'
        : 'Sending failed. Please try again or give us a call.',
  };

  const inquiryTypes = [
    {
      value: 'apitherapy',
      label: currentLang === 'sl' ? 'Rezervacija apiterapije' : 'Apitherapy Booking',
    },
    {
      value: 'product',
      label: currentLang === 'sl' ? 'Naročilo izdelkov' : 'Product Order',
    },
    {
      value: 'general',
      label: currentLang === 'sl' ? 'Splošna poslovna poizvedba' : 'General Business Inquiry',
    },
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = currentLang === 'sl' ? 'Vnesite veljavno ime' : 'Enter a valid name';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = currentLang === 'sl' ? 'Vnesite veljaven email' : 'Enter a valid email';
    }

    const phoneRegex = /^[0-9+\s()-]{8,}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      newErrors.phone =
        currentLang === 'sl' ?'Vnesite veljavno telefonsko številko' :'Enter a valid phone number';
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType =
        currentLang === 'sl' ? 'Izberite vrsto poizvedbe' : 'Select inquiry type';
    }

    if (!formData.message || formData.message.length < 10) {
      newErrors.message =
        currentLang === 'sl' ?'Vnesite sporočilo (vsaj 10 znakov)' :'Enter a message (at least 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || isSending) return;

    setIsSending(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType: 'contact', ...formData }),
      });
      if (!response.ok) throw new Error(`Email request failed: ${response.status}`);

      // Shrani še v admin panel; neuspeh tu ne sme preprečiti oddaje obrazca.
      const { error } = await supabase.from('contact_submissions').insert([
        {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            inquiry_type: formData.inquiryType,
            message: formData.message,
        },
      ]);
      if (error) console.error('Error saving submission:', error);

      setIsSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', inquiryType: '', message: '' });
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(labels.sendError);
    } finally {
      setIsSending(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card card-rounded p-8 shadow-lg space-y-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
            <div className="h-12 bg-muted rounded animate-pulse"></div>
          </div>
        ))}
        <div className="h-12 bg-muted rounded animate-pulse"></div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="bg-success/10 border border-success card-rounded p-8 text-center space-y-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto">
          <Icon name="CheckCircleIcon" size={32} className="text-success" />
        </div>
        <p className="text-lg font-medium">{labels.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card card-rounded p-8 shadow-lg space-y-6">
      {/* Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium">
          {labels.name}
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.name ? 'border-error' : 'border-input'
          }`}
        />
        {errors.name && <p className="text-sm text-error">{errors.name}</p>}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium">
          {labels.email}
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.email ? 'border-error' : 'border-input'
          }`}
        />
        {errors.email && <p className="text-sm text-error">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <label htmlFor="phone" className="text-sm font-medium">
          {labels.phone}
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.phone ? 'border-error' : 'border-input'
          }`}
        />
        {errors.phone && <p className="text-sm text-error">{errors.phone}</p>}
      </div>

      {/* Inquiry Type */}
      <div className="space-y-2">
        <label htmlFor="inquiryType" className="text-sm font-medium">
          {labels.inquiryType}
        </label>
        <select
          id="inquiryType"
          name="inquiryType"
          value={formData.inquiryType}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.inquiryType ? 'border-error' : 'border-input'
          }`}
        >
          <option value="">
            {currentLang === 'sl' ? 'Izberite vrsto poizvedbe' : 'Select inquiry type'}
          </option>
          {inquiryTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {errors.inquiryType && <p className="text-sm text-error">{errors.inquiryType}</p>}
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium">
          {labels.message}
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={6}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all resize-none ${
            errors.message ? 'border-error' : 'border-input'
          }`}
        ></textarea>
        {errors.message && <p className="text-sm text-error">{errors.message}</p>}
      </div>

      {/* Submit Button */}
      {submitError && <p className="text-sm text-error">{submitError}</p>}

      <button
        type="submit"
        disabled={isSending}
        className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSending ? labels.sending : labels.submit}
      </button>
    </form>
  );
}
